<template>
  <div>
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("docs.title") }}</h1>
        <p class="page-head__lede">{{ t("docs.lede") }}</p>
      </div>
    </div>

    <div class="grid grid--2">
      <!--
        GUIDES FIRST, REFERENCE SECOND, and that ordering is the design of this
        page. A developer arriving at an unfamiliar platform does not need the
        list of endpoints — they need to know that authorisation is PKCE-only,
        that webhook signatures cover the raw body, and that review reads their
        scope justifications. The reference answers questions they do not have
        yet.
      -->
      <router-link to="/docs/guides" class="card doc-card">
        <span class="doc-card__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M3 4.5A1.5 1.5 0 0 1 4.5 3H9v14H4.5A1.5 1.5 0 0 1 3 15.5v-11ZM17 4.5A1.5 1.5 0 0 0 15.5 3H11v14h4.5a1.5 1.5 0 0 0 1.5-1.5v-11Z"
                  stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" />
          </svg>
        </span>
        <h2 class="doc-card__title">{{ t("docs.guidesTitle") }}</h2>
        <p class="doc-card__body small muted">{{ t("docs.guidesBody") }}</p>
        <span class="doc-card__go small">{{ t("docs.startHere") }} →</span>
      </router-link>

      <router-link to="/docs/reference" class="card doc-card">
        <span class="doc-card__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M7 6h6M7 10h6M7 14h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
            <rect x="3.5" y="2.5" width="13" height="15" rx="2" stroke="currentColor" stroke-width="1.4" />
          </svg>
        </span>
        <h2 class="doc-card__title">{{ t("docs.referenceTitle") }}</h2>
        <p class="doc-card__body small muted">{{ t("docs.referenceBody") }}</p>
        <span class="doc-card__go small">{{ t("docs.browse") }} →</span>
      </router-link>

      <router-link to="/console" class="card doc-card">
        <span class="doc-card__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <rect x="2.5" y="3.5" width="15" height="13" rx="2" stroke="currentColor" stroke-width="1.4" />
            <path d="m6 8 2.5 2.5L6 13M11 13h3" stroke="currentColor" stroke-width="1.4"
                  stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <h2 class="doc-card__title">{{ t("docs.consoleTitle") }}</h2>
        <p class="doc-card__body small muted">{{ t("docs.consoleBody") }}</p>
        <span class="doc-card__go small">{{ t("docs.openConsole") }} →</span>
      </router-link>

      <router-link to="/docs/changelog" class="card doc-card">
        <span class="doc-card__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="7.5" stroke="currentColor" stroke-width="1.4" />
            <path d="M10 5.5V10l3 2" stroke="currentColor" stroke-width="1.4"
                  stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <h2 class="doc-card__title">{{ t("docs.changelogTitle") }}</h2>
        <p class="doc-card__body small muted">{{ t("docs.changelogBody") }}</p>
        <span class="doc-card__go small">{{ t("docs.seeChanges") }} →</span>
      </router-link>
    </div>

    <div class="card" style="margin-top: var(--space-4)">
      <h2 class="card__title">{{ t("docs.machineReadable") }}</h2>
      <p class="small">
        <a :href="specUrl" target="_blank" rel="noopener noreferrer">openapi.json</a>
        — generate a client from it, or import it into your tooling. A
        <router-link to="/docs/reference">{{ t("reference.postman") }}</router-link>
        can be exported from the reference.
      </p>
      <p class="small muted" style="margin-bottom: 0">
        {{ t("docs.discovery") }}
        <code>/api/ext/v1/openapi.json</code>,
        <code>/api/ext/v1/.well-known/oauth-authorization-server</code>,
        <code>/api/ext/v1/.well-known/jwks.json</code>,
        <code>/api/ext/v1/health</code>.
      </p>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { openApiUrl } from "../lib/openapi";

const { t } = useI18n();
const specUrl = openApiUrl();
</script>

<style scoped>
.doc-card {
  display: block;
  color: inherit;
  text-decoration: none;
  transition: border-color var(--transition), box-shadow var(--transition), transform var(--transition);
}
.doc-card:hover {
  text-decoration: none;
  border-color: var(--brand-200);
  box-shadow: var(--shadow-lg);
  transform: translateY(-1px);
}
.doc-card__icon {
  display: grid;
  place-items: center;
  width: 34px; height: 34px;
  border-radius: var(--radius);
  background: var(--accent-soft);
  color: var(--brand-700);
  margin-bottom: var(--space-3);
}
.doc-card__title { margin: 0 0 var(--space-2); font-size: var(--text-md); }
.doc-card__body { margin: 0 0 var(--space-3); }
.doc-card__go { color: var(--accent); font-weight: 500; }
</style>
